import React from 'react';
import { X, Calculator, DollarSign } from 'lucide-react';

const ResumenLiquidacionModal = ({ isOpen, onClose, resumen }) => {
  if (!isOpen || !resumen) return null;

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '-';
    return Number(num).toLocaleString('es-ES', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const tieneDecomision = resumen.decomision !== null && resumen.decomision !== undefined;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className={`inline-block w-full ${tieneDecomision ? 'max-w-7xl' : 'max-w-4xl'} my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl`}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-500">
            <div className="flex items-center gap-3">
              <Calculator className="w-6 h-6 text-white" />
              <h3 className="text-lg font-semibold text-white">
                {tieneDecomision ? '💰 Resumen de Liquidación y Decomisión' : '💰 Resumen de Liquidación Calculada'}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[calc(100vh-200px)] overflow-y-auto">
            {tieneDecomision ? (
              <div className="grid grid-cols-2 gap-6">
                {/* Columna Liquidación */}
                <div className="border-r border-gray-200 pr-6">
                  <div className="mb-4 pb-2 border-b-2 border-blue-500">
                    <h3 className="text-lg font-bold text-blue-700 flex items-center gap-2">
                      <Calculator className="w-5 h-5" />
                      LIQUIDACIÓN
                    </h3>
                  </div>
                  {resumen.tipo === 'extramargen' ? (
                    <ResumenExtramargen resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                  ) : resumen.tipo === 'fijo' ? (
                    <ResumenFijo resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                  ) : resumen.tipo === 'easy' ? (
                    <ResumenEasy resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                  ) : resumen.tipo === 'volumen_cups' ? (
                    <ResumenVolumenCups resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                  ) : resumen.tipo === 'contrato' ? (
                    <ResumenContrato resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                  ) : (
                    <div className="text-gray-600">Tipo de liquidación no soportado</div>
                  )}
                </div>

                {/* Columna Decomisión */}
                <div className="pl-6">
                  <div className="mb-4 pb-2 border-b-2 border-purple-500">
                    <h3 className="text-lg font-bold text-purple-700 flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      DECOMISIÓN
                    </h3>
                  </div>
                  <ResumenDecomision decomision={resumen.decomision} formatNumber={formatNumber} formatDate={formatDate} />
                </div>
              </div>
            ) : (
              <>
                {resumen.tipo === 'extramargen' ? (
                  <ResumenExtramargen resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                ) : resumen.tipo === 'fijo' ? (
                  <ResumenFijo resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                ) : resumen.tipo === 'easy' ? (
                  <ResumenEasy resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                ) : resumen.tipo === 'volumen_cups' ? (
                  <ResumenVolumenCups resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                ) : resumen.tipo === 'contrato' ? (
                  <ResumenContrato resumen={resumen} formatNumber={formatNumber} formatDate={formatDate} />
                ) : (
                  <div className="text-gray-600">Tipo de liquidación no soportado</div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ResumenExtramargen = ({ resumen, formatNumber, formatDate }) => {
  const { datos_basicos, parametros_comision, consumos_anuales, potencias_contratadas,
    extramargenes_energia, extramargenes_potencia, calculos, liquidacion_final } = resumen;

  return (
    <div className="space-y-6 font-mono text-sm">
      {/* Datos Básicos */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">📋 DATOS BÁSICOS</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-40 text-gray-600">CUPS:</span>
            <span className="font-medium">{datos_basicos.cups}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Cliente:</span>
            <span className="font-medium">{datos_basicos.cliente}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Inicio:</span>
            <span>{formatDate(datos_basicos.fecha_inicio)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Fin:</span>
            <span>{formatDate(datos_basicos.fecha_fin)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Días incluidos:</span>
            <span>{datos_basicos.dias_incluidos} días</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Tipo liquidación:</span>
            <span>{datos_basicos.tipo_liquidacion}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Esquema:</span>
            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">
              {datos_basicos.esquema}
            </span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Origen consumo:</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
              datos_basicos.origen_consumo === 'SIPS' ? 'bg-green-100 text-green-800' :
              datos_basicos.origen_consumo === 'CONTRATO' || datos_basicos.origen_consumo === 'contrato' ? 'bg-blue-100 text-blue-800' :
              datos_basicos.origen_consumo === 'ESTIMADO' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {datos_basicos.origen_consumo}
            </span>
          </div>
        </div>
      </section>

      {/* Parámetros de Comisión */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">💼 PARÁMETROS DE COMISIÓN</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-40 text-gray-600">% Extra Broker:</span>
            <span className="font-semibold text-green-700">{formatNumber(parametros_comision.porcentaje_extra_broker, 2)}%</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Comisión €/MWh:</span>
            <span>{formatNumber(parametros_comision.comision_emwh, 4)} €</span>
          </div>
        </div>
      </section>

      {/* Consumos Anuales Estimados */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">⚡ CONSUMOS ANUALES ESTIMADOS (kWh)</h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-gray-700">
          {[1, 2, 3, 4, 5, 6].map(i => {
            const value = consumos_anuales[`cae_p${i}`];
            return value > 0 ? (
              <div key={i} className="flex">
                <span className="w-24 text-gray-600">CAE P{i}:</span>
                <span className="text-right flex-1">{formatNumber(value, 2)} kWh</span>
              </div>
            ) : null;
          })}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-300 flex font-semibold text-gray-900">
          <span className="w-24">TOTAL:</span>
          <span className="text-right flex-1">{formatNumber(consumos_anuales.total, 2)} kWh</span>
        </div>
      </section>

      {/* Potencias Contratadas */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">🔌 POTENCIAS CONTRATADAS (kW)</h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-gray-700">
          {[1, 2, 3, 4, 5, 6].map(i => {
            const value = potencias_contratadas[`potencia_p${i}`];
            return value > 0 ? (
              <div key={i} className="flex">
                <span className="w-24 text-gray-600">Potencia P{i}:</span>
                <span className="text-right flex-1">{formatNumber(value, 3)} kW</span>
              </div>
            ) : null;
          })}
        </div>
        <div className="mt-2 pt-2 border-t border-gray-300 flex font-semibold text-gray-900">
          <span className="w-24">TOTAL:</span>
          <span className="text-right flex-1">{formatNumber(potencias_contratadas.total, 3)} kW</span>
        </div>
      </section>

      {/* Extramargenes de Energía */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">💡 EXTRAMARGENES DE ENERGÍA (€/kWh)</h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-gray-700">
          {[1, 2, 3, 4, 5, 6].map(i => {
            const value = extramargenes_energia[`extramargen_p${i}`];
            return value !== 0 ? (
              <div key={i} className="flex">
                <span className="w-32 text-gray-600">Extramargen P{i}:</span>
                <span className="text-right flex-1">{formatNumber(value, 6)} €/kWh</span>
              </div>
            ) : null;
          })}
        </div>
      </section>

      {/* Extramargenes de Potencia */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">⚙️ EXTRAMARGENES DE POTENCIA (€/kW/año)</h4>
        <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-gray-700">
          {[1, 2, 3, 4, 5, 6].map(i => {
            const value = extramargenes_potencia[`extramargen_p${i}`];
            return value !== 0 ? (
              <div key={i} className="flex">
                <span className="w-32 text-gray-600">Extramargen P{i}:</span>
                <span className="text-right flex-1">{formatNumber(value, 4)} €/kW/año</span>
              </div>
            ) : null;
          })}
        </div>
      </section>

      {/* Cálculos */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">🧮 CÁLCULOS</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-56 text-gray-600">Margen por Energía:</span>
            <span className="text-right flex-1">{formatNumber(calculos.margen_energia, 2)} €</span>
          </div>
          <div className="flex">
            <span className="w-56 text-gray-600">Margen por Potencia:</span>
            <span className="text-right flex-1">{formatNumber(calculos.margen_potencia, 2)} €</span>
          </div>
          <div className="flex">
            <span className="w-56 text-gray-600">Comisión Especial €/MWh:</span>
            <span className="text-right flex-1">{formatNumber(calculos.comision_especial_emwh, 2)} €</span>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-300 flex font-semibold text-gray-900">
            <span className="w-56">Extramargen Bruto:</span>
            <span className="text-right flex-1">{formatNumber(calculos.extramargen_bruto, 2)} €</span>
          </div>
        </div>
      </section>

      {/* Liquidación Final */}
      <section className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="text-base font-semibold text-green-900 mb-3">💵 LIQUIDACIÓN FINAL</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-64 text-gray-700">Extramargen Bruto × % Broker:</span>
            <span className="text-right flex-1 font-semibold">{formatNumber(liquidacion_final.extramargen_bruto_x_broker, 2)} €</span>
          </div>
          <div className="mt-3 pt-3 border-t border-green-300 flex font-bold text-green-900 text-lg">
            <span className="w-64">✅ TOTAL LIQUIDACIÓN:</span>
            <span className="text-right flex-1">{formatNumber(liquidacion_final.total_liquidacion, 2)} €</span>
          </div>
        </div>
      </section>
    </div>
  );
};

const ResumenFijo = ({ resumen, formatNumber, formatDate }) => {
  const { datos_basicos, parametros_comision, liquidacion_final } = resumen;

  return (
    <div className="space-y-6 font-mono text-sm">
      {/* Datos Básicos */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">📋 DATOS BÁSICOS</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-40 text-gray-600">CUPS:</span>
            <span className="font-medium">{datos_basicos.cups}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Cliente:</span>
            <span className="font-medium">{datos_basicos.cliente}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Inicio:</span>
            <span>{formatDate(datos_basicos.fecha_inicio)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Fin:</span>
            <span>{formatDate(datos_basicos.fecha_fin)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Días incluidos:</span>
            <span>{datos_basicos.dias_incluidos} días</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Tipo liquidación:</span>
            <span>{datos_basicos.tipo_liquidacion}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Esquema:</span>
            <span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">
              {datos_basicos.esquema}
            </span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Origen consumo:</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
              datos_basicos.origen_consumo === 'SIPS' ? 'bg-green-100 text-green-800' :
              datos_basicos.origen_consumo === 'CONTRATO' || datos_basicos.origen_consumo === 'contrato' ? 'bg-blue-100 text-blue-800' :
              datos_basicos.origen_consumo === 'ESTIMADO' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {datos_basicos.origen_consumo}
            </span>
          </div>
        </div>
      </section>

      {/* Parámetros de Comisión */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">💼 PARÁMETROS DE COMISIÓN</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-56 text-gray-600">Comisión Fija por CUPS:</span>
            <span className="text-right flex-1 font-semibold text-green-700">{formatNumber(parametros_comision.comision_fija_ecups, 2)} €</span>
          </div>
        </div>
      </section>

      {/* Liquidación Final */}
      <section className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h4 className="text-base font-semibold text-green-900 mb-3">💵 LIQUIDACIÓN FINAL</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-56 text-gray-700">Comisión Fija por CUPS:</span>
            <span className="text-right flex-1 font-semibold">{formatNumber(liquidacion_final.comision_fija, 2)} €</span>
          </div>
          <div className="mt-3 pt-3 border-t border-green-300 flex font-bold text-green-900 text-lg">
            <span className="w-56">✅ TOTAL LIQUIDACIÓN:</span>
            <span className="text-right flex-1">{formatNumber(liquidacion_final.total_liquidacion, 2)} €</span>
          </div>
        </div>
      </section>
    </div>
  );
};

const ResumenContrato = ({ resumen, formatNumber, formatDate }) => {
  const { datos_basicos, contrato_info, liquidacion_final } = resumen;

  return (
    <div className="space-y-6 font-mono text-sm">
      {/* Datos Básicos */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">📋 DATOS BÁSICOS</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-40 text-gray-600">CUPS:</span>
            <span className="font-medium">{datos_basicos.cups}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Cliente:</span>
            <span className="font-medium">{datos_basicos.cliente}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Inicio:</span>
            <span>{formatDate(datos_basicos.fecha_inicio)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Fin:</span>
            <span>{formatDate(datos_basicos.fecha_fin)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Días incluidos:</span>
            <span>{datos_basicos.dias_incluidos} días</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Tipo liquidación:</span>
            <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs font-semibold">
              {datos_basicos.tipo_liquidacion}
            </span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Origen consumo:</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
              datos_basicos.origen_consumo === 'SIPS' ? 'bg-green-100 text-green-800' :
              datos_basicos.origen_consumo === 'CONTRATO' || datos_basicos.origen_consumo === 'contrato' ? 'bg-blue-100 text-blue-800' :
              datos_basicos.origen_consumo === 'ESTIMADO' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {datos_basicos.origen_consumo}
            </span>
          </div>
        </div>
      </section>

      {/* Información del Contrato */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">📄 CONTRATO APLICADO</h4>
        <div className="space-y-2 text-gray-700">
          <div className="flex items-center">
            <span className="w-40 text-gray-600">Nombre:</span>
            <span className="flex-1 font-semibold text-orange-700 px-3 py-1.5 bg-orange-50 rounded border border-orange-200">
              {contrato_info.nombre_contrato}
            </span>
          </div>
        </div>
      </section>

      {/* Fórmula Aplicada */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">🧮 FÓRMULA DE CÁLCULO</h4>
        <div className="space-y-3">
          <div className="bg-gray-50 border border-gray-300 rounded p-3">
            <div className="text-xs text-gray-500 mb-1">Fórmula:</div>
            <code className="text-sm text-gray-900 break-all font-mono">
              {contrato_info.formula}
            </code>
          </div>
        </div>
      </section>

      {/* Variables de la Fórmula */}
      {contrato_info.variables && contrato_info.variables.length > 0 && (
        <section className="border-b border-gray-200 pb-4">
          <h4 className="text-base font-semibold text-gray-900 mb-3">📊 VALORES DE LAS VARIABLES</h4>
          <div className="space-y-2">
            {contrato_info.variables.map((variable, idx) => {
              let valorFormateado = variable.valor;
              
              // Formatear según el tipo
              if (variable.tipo === 'numeric' && variable.valor !== null) {
                valorFormateado = formatNumber(variable.valor, 2);
              } else if (variable.tipo === 'boolean') {
                valorFormateado = variable.valor ? 'Sí' : 'No';
              } else if (variable.valor === null) {
                valorFormateado = 'N/A';
              }

              return (
                <div key={idx} className="flex items-center bg-blue-50 px-3 py-2 rounded border border-blue-200">
                  <span className="text-blue-700 font-mono text-sm w-64">
                    #{variable.nombre}
                  </span>
                  <span className="text-gray-700 font-semibold">
                    = {valorFormateado}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Liquidación Final */}
      <section className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <h4 className="text-base font-semibold text-orange-900 mb-3">💵 LIQUIDACIÓN FINAL</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex items-center text-xs text-gray-500 mb-2">
            <span className="w-full text-center italic">
              Resultado de evaluar la fórmula con los valores mostrados arriba
            </span>
          </div>
          <div className="mt-3 pt-3 border-t border-orange-300 flex font-bold text-orange-900 text-lg">
            <span className="w-56">✅ TOTAL LIQUIDACIÓN:</span>
            <span className="text-right flex-1">{formatNumber(liquidacion_final.total_liquidacion, 2)} €</span>
          </div>
        </div>
      </section>
    </div>
  );
};

const ResumenDecomision = ({ decomision, formatNumber, formatDate }) => {
  if (!decomision) {
    return (
      <div className="text-center text-gray-500 py-8">
        No hay datos de decomisión disponibles
      </div>
    );
  }

  return (
    <div className="space-y-6 font-mono text-sm">
      {/* Estado de Decomisión */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">📊 ESTADO</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-40 text-gray-600">Estado:</span>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-semibold uppercase">
              {decomision.estado}
            </span>
          </div>
          {decomision.fecha_calculo && (
            <div className="flex">
              <span className="w-40 text-gray-600">Fecha Cálculo:</span>
              <span>{formatDate(decomision.fecha_calculo)}</span>
            </div>
          )}
        </div>
      </section>

      {/* Datos de Liquidación Original */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">📋 LIQUIDACIÓN ORIGINAL</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-48 text-gray-600">Total Liquidación:</span>
            <span className="text-right flex-1 font-semibold">{formatNumber(decomision.liquidacion_total, 2)} €</span>
          </div>
          <div className="flex">
            <span className="w-48 text-gray-600">Días Liquidación:</span>
            <span className="text-right flex-1">{decomision.dias_liquidacion} días</span>
          </div>
          <div className="flex">
            <span className="w-48 text-gray-600">Tarifa Diaria:</span>
            <span className="text-right flex-1">{formatNumber(decomision.tarifa_diaria, 4)} €/día</span>
          </div>
        </div>
      </section>

      {/* Cálculo de Decomisión */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">🧮 CÁLCULO</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-48 text-gray-600">Días a Decomisionar:</span>
            <span className="text-right flex-1 font-semibold text-purple-700">{decomision.dias_decomision} días</span>
          </div>
          <div className="flex items-center text-xs text-gray-500 mt-2">
            <span className="w-full text-center">
              Fórmula: −[(Liquidación ÷ Días Liq.) × Días Decom.]
            </span>
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <span className="w-full text-center">
              −[({formatNumber(decomision.liquidacion_total, 2)} ÷ {decomision.dias_liquidacion}) × {decomision.dias_decomision}] = {formatNumber(decomision.decomision_total, 2)} €
            </span>
          </div>
        </div>
      </section>

      {/* Resultado de Decomisión */}
      <section className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="text-base font-semibold text-purple-900 mb-3">💸 DECOMISIÓN CALCULADA</h4>
        <div className="space-y-2 text-gray-700">
          <div className="flex">
            <span className="w-56 text-gray-700">Monto a Decomisionar:</span>
            <span className="text-right flex-1 font-semibold text-red-600">{formatNumber(decomision.decomision_total, 2)} €</span>
          </div>
          <div className="flex">
            <span className="w-56 text-gray-700">Porcentaje Decomisionado:</span>
            <span className="text-right flex-1 font-semibold text-red-600">{formatNumber(decomision.porcentaje_decomisionado, 2)}%</span>
          </div>
          <div className="mt-3 pt-3 border-t border-purple-300 flex font-bold text-green-900 text-lg">
            <span className="w-56">✅ NETO FINAL:</span>
            <span className="text-right flex-1">{formatNumber(decomision.neto_final, 2)} €</span>
          </div>
        </div>
      </section>

      {/* Info adicional */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-xs text-gray-700">
        <p className="font-semibold mb-1">ℹ️ Información:</p>
        <p>
          La decomisión calcula el monto proporcional a devolver basándose en los días que el contrato 
          terminó anticipadamente respecto a la liquidación calculada.
        </p>
      </div>
    </div>
  );
};

const ResumenEasy = ({ resumen, formatNumber, formatDate }) => {
  const { datos_basicos, parametros_easy, liquidacion_final } = resumen;

  return (
    <div className="space-y-6 font-mono text-sm">
      {/* Datos Básicos */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">📋 DATOS BÁSICOS</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-40 text-gray-600">CUPS:</span>
            <span className="font-medium">{datos_basicos.cups}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Cliente:</span>
            <span className="font-medium">{datos_basicos.cliente}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Inicio:</span>
            <span>{formatDate(datos_basicos.fecha_inicio)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Fin:</span>
            <span>{formatDate(datos_basicos.fecha_fin)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Días incluidos:</span>
            <span>{datos_basicos.dias_incluidos} días</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Tipo liquidación:</span>
            <span>{datos_basicos.tipo_liquidacion}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Esquema:</span>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs font-semibold">
              {datos_basicos.esquema}
            </span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Origen consumo:</span>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
              datos_basicos.origen_consumo === 'SIPS' ? 'bg-green-100 text-green-800' :
              datos_basicos.origen_consumo === 'CONTRATO' || datos_basicos.origen_consumo === 'contrato' ? 'bg-blue-100 text-blue-800' :
              datos_basicos.origen_consumo === 'ESTIMADO' ? 'bg-yellow-100 text-yellow-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {datos_basicos.origen_consumo}
            </span>
          </div>
        </div>
      </section>

      {/* Parámetros EASY */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">💼 PARÁMETROS EASY</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-56 text-gray-600">Tipo EASY:</span>
            <span className="text-right flex-1 font-semibold text-purple-700">{parametros_easy.tipo_easy}</span>
          </div>
          <div className="flex">
            <span className="w-56 text-gray-600">Nivel EASY:</span>
            <span className="text-right flex-1 font-semibold text-purple-700">{parametros_easy.nivel_easy} €/MWh</span>
          </div>
          <div className="flex">
            <span className="w-56 text-gray-600">Consumo Anual:</span>
            <span className="text-right flex-1 font-semibold">{formatNumber(parametros_easy.consumo_anual_kwh, 2)} kWh</span>
          </div>
          <div className="flex">
            <span className="w-56 text-gray-600">Consumo Anual (MWh):</span>
            <span className="text-right flex-1">{formatNumber(parametros_easy.consumo_anual_kwh / 1000, 3)} MWh</span>
          </div>
        </div>
      </section>

      {/* Liquidación Final */}
      <section className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <h4 className="text-base font-semibold text-purple-900 mb-3">💵 LIQUIDACIÓN FINAL</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-56 text-gray-700">Comisión EASY:</span>
            <span className="text-right flex-1 font-semibold">{formatNumber(liquidacion_final.comision_easy, 2)} €</span>
          </div>
          <div className="mt-3 pt-3 border-t border-purple-300 flex font-bold text-purple-900 text-lg">
            <span className="w-56">✅ TOTAL LIQUIDACIÓN:</span>
            <span className="text-right flex-1">{formatNumber(liquidacion_final.total_liquidacion, 2)} €</span>
          </div>
        </div>
      </section>

      {/* Info adicional */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-xs text-gray-700">
        <p className="font-semibold mb-1">ℹ️ Información:</p>
        <p>
          El esquema EASY calcula una comisión fija basada en el consumo anual del cliente y 
          las tarifas según el tipo y nivel EASY contratado.
        </p>
      </div>
    </div>
  );
};

const ResumenVolumenCups = ({ resumen, formatNumber, formatDate }) => {
  const { datos_basicos, parametros_volumen, liquidacion_final } = resumen;

  return (
    <div className="space-y-6 font-mono text-sm">
      {/* Datos Básicos */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">📋 DATOS BÁSICOS</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-40 text-gray-600">CUPS:</span>
            <span className="font-medium">{datos_basicos.cups}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Cliente:</span>
            <span className="font-medium">{datos_basicos.cliente}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Inicio:</span>
            <span>{formatDate(datos_basicos.fecha_inicio)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Fecha Fin:</span>
            <span>{formatDate(datos_basicos.fecha_fin)}</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Días incluidos:</span>
            <span>{datos_basicos.dias_incluidos} días</span>
          </div>
          <div className="flex">
            <span className="w-40 text-gray-600">Esquema:</span>
            <span className="px-2 py-0.5 bg-teal-100 text-teal-800 rounded text-xs font-semibold">
              {datos_basicos.esquema}
            </span>
          </div>
        </div>
      </section>

      {/* Parámetros de Volumen */}
      <section className="border-b border-gray-200 pb-4">
        <h4 className="text-base font-semibold text-gray-900 mb-3">💼 PARÁMETROS DE VOLUMEN</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-56 text-gray-600">Canal:</span>
            <span className="text-right flex-1 font-semibold text-teal-700">{parametros_volumen.canal}</span>
          </div>
          <div className="flex">
            <span className="w-56 text-gray-600">Periodo (mes/año):</span>
            <span className="text-right flex-1 font-semibold text-teal-700">{parametros_volumen.periodo}</span>
          </div>
        </div>
      </section>

      {/* Liquidación Final */}
      <section className="bg-teal-50 border border-teal-200 rounded-lg p-4">
        <h4 className="text-base font-semibold text-teal-900 mb-3">💵 LIQUIDACIÓN FINAL</h4>
        <div className="space-y-1 text-gray-700">
          <div className="flex">
            <span className="w-56 text-gray-700">Comisión por tramo de volumen:</span>
            <span className="text-right flex-1 font-semibold">{formatNumber(liquidacion_final.comision_volumen, 2)} €</span>
          </div>
          <div className="mt-3 pt-3 border-t border-teal-300 flex font-bold text-teal-900 text-lg">
            <span className="w-56">✅ TOTAL LIQUIDACIÓN:</span>
            <span className="text-right flex-1">{formatNumber(liquidacion_final.total_liquidacion, 2)} €</span>
          </div>
        </div>
      </section>

      {/* Info adicional */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-3 text-xs text-gray-700">
        <p className="font-semibold mb-1">ℹ️ Información:</p>
        <p>
          El esquema de volumen de CUPS paga un importe fijo por CUPS según el tramo
          alcanzado por el número de CUPS activados en el mes natural, agrupados por canal.
          El conteo usado en el cálculo no se conserva históricamente (es un snapshot en
          el momento del cálculo), solo el importe final.
        </p>
      </div>
    </div>
  );
};

export default ResumenLiquidacionModal;
